import { Routes, Route, Navigate } from 'react-router-dom';
import { LandingPage } from './pages/public/LandingPage';
import { PricingPage } from './pages/public/PricingPage';
import { ComparePage } from './pages/public/ComparePage';
import { SecurityPage } from './pages/public/SecurityPage';
import { StatusPage } from './pages/public/StatusPage';
import { DemoPage } from './pages/public/DemoPage';
import { NotFoundPage } from './pages/public/NotFoundPage';
import { SharedSnapshotPage } from './pages/share/SharedSnapshotPage';
import { AdminQueuePage } from './pages/admin/AdminQueuePage';
import { AdminOverviewPage } from './pages/admin/AdminOverviewPage';
import { AdminUsersPage } from './pages/admin/AdminUsersPage';
import { AdminWorkspacesPage } from './pages/admin/AdminWorkspacesPage';
import { AdminJobsPage } from './pages/admin/AdminJobsPage';
import { AdminAuditPage } from './pages/admin/AdminAuditPage';
import { AdminIncidentsPage } from './pages/admin/AdminIncidentsPage';
import { AdminEmailLogPage } from './pages/admin/AdminEmailLogPage';
import { AdminRoute } from './components/AdminRoute';
import { ClosePacketPage } from './pages/app/ClosePacketPage';
import { OnboardingPage } from './pages/onboarding/OnboardingPage';
import { AppHome } from './pages/app/AppHome';
import { HomePage } from './pages/app/HomePage';
import { ProfilePage } from './pages/app/ProfilePage';
import { InboxPage } from './pages/app/InboxPage';
import { InvoicesApPage } from './pages/app/InvoicesApPage';
import { InvoicesArPage } from './pages/app/InvoicesArPage';
import { VendorsPage } from './pages/app/VendorsPage';
import { ExpensesPage } from './pages/app/ExpensesPage';
import { CardsPage } from './pages/app/CardsPage';
import { PaymentsPage } from './pages/app/PaymentsPage';
import { ApprovalsPage } from './pages/app/ApprovalsPage';
import { ClosingPage } from './pages/app/ClosingPage';
import { LedgerPage } from './pages/app/LedgerPage';
import { TaxUsPage } from './pages/app/TaxUsPage';
import { ReportsPage } from './pages/app/ReportsPage';
import { BudgetsPage } from './pages/app/BudgetsPage';
import { AgentsPage } from './pages/app/AgentsPage';
import { PeppolPage } from './pages/app/PeppolPage';
import { AuditLogPage } from './pages/app/AuditLogPage';
import { SettingsPage } from './pages/app/SettingsPage';
import { BankFeedPage } from './pages/app/BankFeedPage';
import { DigitalRecordsPage } from './pages/app/DigitalRecordsPage';
import { MatchingPage } from './pages/app/MatchingPage';
import { CollectionsPage } from './pages/app/CollectionsPage';
import { LoginPage } from './pages/auth/LoginPage';
import { SignupPage } from './pages/auth/SignupPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { VerifyEmailPage } from './pages/auth/VerifyEmailPage';
import { TermsPage } from './pages/legal/TermsPage';
import { PrivacyPage } from './pages/legal/PrivacyPage';
import { CookiePolicyPage } from './pages/legal/CookiePolicyPage';
import { DpaPage } from './pages/legal/DpaPage';
import { SubprocessorsPage } from './pages/legal/SubprocessorsPage';
import { AcceptableUsePage } from './pages/legal/AcceptableUsePage';
import { DataSourcingPage } from './pages/legal/DataSourcingPage';
import { SlaPage } from './pages/legal/SlaPage';
import { ChangelogPage } from './pages/public/ChangelogPage';
import { AboutPage } from './pages/public/AboutPage';
import { ContactPage } from './pages/public/ContactPage';
import { CareersPage } from './pages/public/CareersPage';
import { OssCreditsPage } from './pages/public/OssCreditsPage';
import { VendorOnboardPage } from './pages/public/VendorOnboardPage';
import { MobileScanPage } from './pages/public/MobileScanPage';
import { GeoLocaleInitializer } from './i18n/GeoLocaleInitializer';
import { ErrorBoundary } from './components/ErrorBoundary';

export default function App() {
  return (
    <>
    <GeoLocaleInitializer />
    <ErrorBoundary>
    <Routes>
      {/* Public */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/compare" element={<ComparePage />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/share/:slug" element={<SharedSnapshotPage />} />
      <Route path="/vendor/onboard/:token" element={<VendorOnboardPage />} />
      <Route path="/m/scan/:token" element={<MobileScanPage />} />

      {/* Public — info / company */}
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/changelog" element={<ChangelogPage />} />
      <Route path="/oss" element={<OssCreditsPage />} />

      {/* Public — legal */}
      <Route path="/legal/terms" element={<TermsPage />} />
      <Route path="/legal/privacy" element={<PrivacyPage />} />
      <Route path="/legal/cookies" element={<CookiePolicyPage />} />
      <Route path="/legal/dpa" element={<DpaPage />} />
      <Route path="/legal/subprocessors" element={<SubprocessorsPage />} />
      <Route path="/legal/aup" element={<AcceptableUsePage />} />
      <Route path="/legal/data-sourcing" element={<DataSourcingPage />} />
      <Route path="/legal/sla" element={<SlaPage />} />
      {/* Legacy alias used by older marketing emails */}
      <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
      <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
      <Route path="/cookies" element={<Navigate to="/legal/cookies" replace />} />
      <Route path="/dpa" element={<Navigate to="/legal/dpa" replace />} />
      <Route path="/subprocessors" element={<Navigate to="/legal/subprocessors" replace />} />
      <Route path="/aup" element={<Navigate to="/legal/aup" replace />} />

      {/* Auth */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/register" element={<Navigate to="/signup" replace />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      {/* Onboarding */}
      <Route path="/onboarding" element={<OnboardingPage />} />

      {/* App */}
      <Route path="/app" element={<AppHome />} />
      <Route path="/app/profile" element={<ProfilePage />} />
      <Route path="/app/workspace/:wsId" element={<Navigate to="home" replace />} />
      <Route path="/app/workspace/:wsId/home" element={<HomePage />} />
      <Route path="/app/workspace/:wsId/inbox" element={<InboxPage />} />
      <Route path="/app/workspace/:wsId/ap-invoices" element={<InvoicesApPage />} />
      <Route path="/app/workspace/:wsId/ar-invoices" element={<InvoicesArPage />} />
      <Route path="/app/workspace/:wsId/vendors" element={<VendorsPage />} />
      <Route path="/app/workspace/:wsId/expenses" element={<ExpensesPage />} />
      <Route path="/app/workspace/:wsId/cards" element={<CardsPage />} />
      <Route path="/app/workspace/:wsId/payments" element={<PaymentsPage />} />
      <Route path="/app/workspace/:wsId/approvals" element={<ApprovalsPage />} />
      <Route path="/app/workspace/:wsId/closing" element={<ClosingPage />} />
      <Route path="/app/workspace/:wsId/close-packet" element={<ClosePacketPage />} />
      <Route path="/app/workspace/:wsId/ledger" element={<LedgerPage />} />
      <Route path="/app/workspace/:wsId/matching" element={<MatchingPage />} />
      <Route path="/app/workspace/:wsId/collections" element={<CollectionsPage />} />
      <Route path="/app/workspace/:wsId/tax-us" element={<TaxUsPage />} />
      <Route path="/app/workspace/:wsId/reports" element={<ReportsPage />} />
      <Route path="/app/workspace/:wsId/budgets" element={<BudgetsPage />} />
      <Route path="/app/workspace/:wsId/agents" element={<AgentsPage />} />
      <Route path="/app/workspace/:wsId/peppol" element={<PeppolPage />} />
      <Route path="/app/workspace/:wsId/bank-feed" element={<BankFeedPage />} />
      <Route path="/app/workspace/:wsId/digital-records" element={<DigitalRecordsPage />} />
      <Route path="/app/workspace/:wsId/audit" element={<AuditLogPage />} />
      <Route path="/app/workspace/:wsId/settings" element={<SettingsPage />} />

      {/* Internal admin */}
      <Route path="/admin" element={<AdminRoute><AdminOverviewPage /></AdminRoute>} />
      <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
      <Route path="/admin/workspaces" element={<AdminRoute><AdminWorkspacesPage /></AdminRoute>} />
      <Route path="/admin/queue" element={<AdminRoute><AdminQueuePage /></AdminRoute>} />
      <Route path="/admin/jobs" element={<AdminRoute><AdminJobsPage /></AdminRoute>} />
      <Route path="/admin/incidents" element={<AdminRoute><AdminIncidentsPage /></AdminRoute>} />
      <Route path="/admin/email-log" element={<AdminRoute><AdminEmailLogPage /></AdminRoute>} />
      <Route path="/admin/audit" element={<AdminRoute><AdminAuditPage /></AdminRoute>} />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </ErrorBoundary>
    </>
  );
}
